import React, { useState, useEffect, useMemo } from 'react';
import './LayerLoaderModal.css';
import { LayerConfig } from '../../../types/metadata';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';
import { createSvgIcon } from '../utils/svgUtils';
import { metadataService } from '../../../services/metadataService';


interface LayerLoaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadLayers: (layers: LayerConfig[]) => void;
  loadedLayerIds: string[];
}

type SortKey = 'name' | 'fields' | 'last_updated' | 'version';
type SortDirection = 'asc' | 'desc';

export const LayerLoaderModal: React.FC<LayerLoaderModalProps> = ({
  isOpen,
  onClose,
  onLoadLayers,
  loadedLayerIds,
}) => {
  const [layers, setLayers] = useState<LayerConfig[]>([]);
  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (isOpen) {
      fetchLayers();
      setSelectedLayerIds(new Set());
    }
  }, [isOpen]);

  const fetchLayers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await metadataService.listMetadata();
      
      // Transform API response to LayerConfig format, excluding already loaded layers
      const transformedLayers: LayerConfig[] = response.files
        .filter((file) => !loadedLayerIds.includes(file.id))
        .map((file) => ({
          ...file,
          visible: true,
          pointSettings: file.pointSettings || DEFAULT_POINT_SETTINGS,
        }));
      
      setLayers(transformedLayers);
    } catch (err) {
      console.error('Failed to fetch layers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load layers';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return <span className="sort-indicator inactive">⇅</span>;
    return <span className="sort-indicator active">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortedLayers = useMemo(() => {
    if (!sortKey) return layers;
    return [...layers].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'fields': {
          const aFields = a.schema ? Object.keys(a.schema).length : 0;
          const bFields = b.schema ? Object.keys(b.schema).length : 0;
          cmp = aFields - bFields;
          break;
        }
        case 'last_updated':
          cmp = new Date(a.last_updated).getTime() - new Date(b.last_updated).getTime();
          break;
        case 'version':
          cmp = a.version - b.version;
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [layers, sortKey, sortDirection]);

  const handleRowClick = (layerId: string) => {
    setSelectedLayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  };

  const handleRowDoubleClick = (layerId: string) => {
    console.log('[LayerLoaderModal] Double-click on layer:', layerId);
    const layer = layers.find(l => l.id === layerId);
    console.log('[LayerLoaderModal] Found layer:', layer);
    if (layer) {
      console.log('[LayerLoaderModal] Calling onLoadLayers with:', [layer]);
      onLoadLayers([layer]);
      onClose();
    } else {
      console.warn('[LayerLoaderModal] Layer not found for id:', layerId);
    }
  };

  const handleOpenClick = () => {
    if (selectedLayerIds.size > 0) {
      const selectedLayers = layers.filter(l => selectedLayerIds.has(l.id));
      if (selectedLayers.length > 0) {
        onLoadLayers(selectedLayers);
        onClose();
      }
    }
  };

  const handleUpload = () => {
    // TODO: Implement upload functionality
    console.log('Upload clicked');
  };

  const handleDelete = () => {
    if (selectedLayerIds.size > 0) {
      // TODO: Implement delete functionality
      console.log('Delete clicked for layers:', Array.from(selectedLayerIds));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="layer-loader-overlay" onClick={onClose}>
      <div className="layer-loader-modal" onClick={(e) => e.stopPropagation()}>
        <div className="layer-loader-header">
          <h2>Load Layer</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="layer-loader-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <span className="loading-text">Loading...</span>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              {error.includes('API URL is not configured') && (
                <div className="error-help">
                  <p>To configure the API:</p>
                  <ol>
                    <li>Run: <code>cd web && ./setup-env.sh</code></li>
                    <li>Or manually create <code>web/.env.local</code> with:<br/>
                        <code>REACT_APP_API_URL=your-api-url</code>
                    </li>
                    <li>Restart the development server</li>
                  </ol>
                </div>
              )}
              <button className="retry-btn" onClick={fetchLayers}>Retry</button>
            </div>
          ) : (
            <table className="layers-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Icon</th>
                  <th className="sortable-header" onClick={() => handleSort('name')}>
                    Name {getSortIndicator('name')}
                  </th>
                  <th>Count</th>
                  <th className="sortable-header" onClick={() => handleSort('fields')}>
                    Fields {getSortIndicator('fields')}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('last_updated')}>
                    Last Updated {getSortIndicator('last_updated')}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('version')}>
                    Version {getSortIndicator('version')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLayers.map((layer) => {
                  const pointSettings = layer.type === 'points' 
                    ? { ...DEFAULT_POINT_SETTINGS, ...layer.pointSettings }
                    : null;
                  
                  const svgIcon = ((pointSettings?.markerType === 'icon' || pointSettings?.markerType === 'raw') && pointSettings?.svg)
                    ? createSvgIcon({
                        svg: pointSettings.svg,
                        size: 20,
                        color: pointSettings.color!,
                        backgroundColor: pointSettings.borderColor ?? [255,255,255]
                      })
                    : '';
                  
                  return (
                    <tr
                      key={layer.id}
                      className={selectedLayerIds.has(layer.id) ? 'selected' : ''}
                      onClick={() => handleRowClick(layer.id)}
                      onDoubleClick={() => handleRowDoubleClick(layer.id)}
                    >
                      <td>
                        {layer.type === 'points' ? (
                          <span title="Points">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="6" cy="6" r="2" />
                              <circle cx="12" cy="8" r="2.5" />
                              <circle cx="18" cy="7" r="1.5" />
                              <circle cx="8" cy="14" r="1.5" />
                              <circle cx="14" cy="16" r="2" />
                              <circle cx="17" cy="18" r="2.5" />
                            </svg>
                          </span>
                        ) : layer.type === 'polygons' ? (
                          <span title="Polygons">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="12,4 20,8 20,16 12,20 4,16 4,8" />
                            </svg>
                          </span>
                        ) : (
                          <span>{layer.type}</span>
                        )}
                      </td>
                      <td>
                        {layer.type === 'points' && pointSettings ? (
                          (pointSettings.markerType === 'icon' || pointSettings.markerType === 'raw') && pointSettings.svg ? (
                            <div dangerouslySetInnerHTML={{ __html: svgIcon }} />
                          ) : (
                            <svg 
                              width="20" 
                              height="20" 
                              viewBox="0 0 24 24" 
                              fill={`rgb(${pointSettings.color!.join(',')})`} 
                              stroke={pointSettings.borderColor ? `rgb(${pointSettings.borderColor.join(',')})` : 'none'} 
                              strokeWidth={pointSettings.borderColor ? pointSettings.borderSize : 0} 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td>{layer.name}</td>
                      <td>{layer.count.toLocaleString()}</td>
                      <td>
                        {layer.schema ? (
                          <span title={Object.entries(layer.schema).map(([field, schemaEntry]) => {
                            const type = typeof schemaEntry === 'object' ? schemaEntry.type : schemaEntry;
                            const isUnique = typeof schemaEntry === 'object' && schemaEntry.isUnique ? ' (Unique)' : '';
                            return `${field} [${type}]${isUnique}`;
                          }).join(', ')}>
                            {Object.keys(layer.schema).length}
                          </span>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td>{new Date(layer.last_updated).toLocaleDateString()}</td>
                      <td>{layer.version}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="layer-loader-footer">
          <div className="footer-left">
            <button className="footer-btn" onClick={handleUpload} disabled title="Coming soon">
              Upload
            </button>
            <button className="footer-btn" onClick={handleDelete} disabled title="Coming soon">
              Delete
            </button>
          </div>
          <div className="footer-right">
            <button className="footer-btn primary" onClick={handleOpenClick} disabled={selectedLayerIds.size === 0}>
              Open{selectedLayerIds.size > 1 ? ` (${selectedLayerIds.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
